/**
 * Calendar-arithmetic helpers that intentionally avoid the
 * `new Date(iso) → toISOString()` round-trip. Doing date math via ISO
 * strings goes through UTC, which silently drops or adds a day in any
 * tz with a non-zero UTC offset — e.g. BST (UTC+1). Components-in,
 * components-out keeps the arithmetic local-only.
 *
 * History: an earlier implementation of `addDays` used
 * `new Date(date + 'T00:00:00').toISOString().slice(0,10)` and
 * silently produced same-day results during BST. Caused multi-day
 * all-day events to render as single-day on the calendar.
 * Re-implemented + unit-tested on 2026-05-21.
 */

/** Add (or subtract, when `days` is negative) calendar days to a
 * YYYY-MM-DD string, returning a YYYY-MM-DD string. Handles month
 * and year wraps; DST-safe because it operates on calendar
 * components only. */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  const dt = new Date(y, m - 1, d + days);
  const y2 = dt.getFullYear();
  const m2 = String(dt.getMonth() + 1).padStart(2, '0');
  const d2 = String(dt.getDate()).padStart(2, '0');
  return `${y2}-${m2}-${d2}`;
}
